import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Alert,
  Chip,
} from '@mui/material';
import { Add as AddIcon} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import api from '../../utils/axios';
import Toast from '../../components/common/Toast';

const TOTAL_STEPS = 2;

const taskStatusColors = {
  not_started: 'default',
  not_achieved: 'error',
  in_progress: 'warning',
  achieved: 'success'
};

const priorityColors = {
  critical: 'error',
  high: 'warning',
  medium: 'primary',
  low: 'default',
};

const priorityLabels = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const KPIPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  const isCoach = user?.role === 'coach';
  const isManager = user?.role === 'manager';
  const isCompanyAdmin = user?.role === 'companyAdmin';

  const [kpiName, setKpiName] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [areas, setAreas] = useState([]);
  const [currentArea, setCurrentArea] = useState('');
  const [comments, setComments] = useState({});
  const [tasks, setTasks] = useState({});
  const [reviewDate, setReviewDate] = useState(null);
  const [currentTasks, setCurrentTasks] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (isCoach) {
      api.get('/companies').then(res => setCompanies(res.data)).catch(() => {});
    }
    if (isCoach || isManager || isCompanyAdmin) {
      api.get('/users').then(res => setAllUsers(res.data)).catch(() => {});
    }
  }, [isCoach, isManager, isCompanyAdmin]);

  const usersInSelectedCompany = allUsers.filter(
    u => u.company?._id === selectedCompanyId || u.company === selectedCompanyId
  );

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  const showToast = (message, severity = 'success') => {
    setToast({
      open: true,
      message,
      severity
    });
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!kpiName.trim()) {
        showToast('KPI name is required', 'error');
        return;
      }
      if (areas.length === 0) {
        showToast('At least one area is required', 'error');
        return;
      }
    }
    if (activeStep === TOTAL_STEPS - 1) {
      if (isCoach && selectedUserId && !selectedCompanyId) {
        showToast('Please select a company to assign the KPI to a user', 'error');
        return;
      }

      if (!reviewDate) {
        showToast('Manager Review Date is required', 'error');
        return;
      }
      await saveKPI();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const addArea = () => {
    if (currentArea.trim()) {
      setAreas([...areas, currentArea.trim()]);
      setCurrentArea('');
      setCurrentTasks(prev => ({ ...prev, [currentArea.trim()]: '' }));
    }
  };

  const addTask = (area) => {
    if (currentTasks[area]?.trim()) {
      const newTask = {
        name: currentTasks[area].trim(),
        status: 'not_started',
        priority: 'low',
      };
      setTasks({ ...tasks, [area]: [...(tasks[area] || []), newTask] });
      setCurrentTasks(prev => ({ ...prev, [area]: '' }));
    }
  };

  const updateComment = (area, value) => {
    setComments({ ...comments, [area]: value });
  };

  const updateTaskStatus = (area, taskIndex, newStatus) => {
    const updatedTasks = { ...tasks };
    updatedTasks[area][taskIndex].status = newStatus;
    setTasks(updatedTasks);
  };

  const updateTaskInput = (area, value) => {
    setCurrentTasks(prev => ({
      ...prev,
      [area]: value
    }));
  };

  const saveKPI = async () => {
    try {
      setLoading(true);
      setError(null);

      const kpiData = {
        name: kpiName.trim(),
        reviewDate,
        areas: areas.map(area => ({
          name: area,
          description: comments[area] || '',
          tasks: tasks[area] || [],
        }))
      };

      if (isCoach) {
        kpiData.companyId = selectedCompanyId;
        if (selectedUserId) {
          kpiData.assignedUserId = selectedUserId;
          kpiData.isTemplate = saveAsTemplate;
        }
      } else if (isManager || isCompanyAdmin) {
        if (selectedUserId) {
          kpiData.assignedUserId = selectedUserId;
        }
      }

      await api.post('/kpi', kpiData);
      showToast('KPI created successfully');
      navigate('/my-kpis');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save KPI';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              required
              label="KPI Name"
              placeholder="e.g. Q2 Performance Goals"
              value={kpiName}
              onChange={(e) => setKpiName(e.target.value)}
              sx={{ mb: 3 }}
              helperText="Give this KPI a unique name so you can identify and search for it"
            />
            <Divider sx={{ mb: 3 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              KPI Metrics
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add the main areas this KPI will cover. Each area will have its own tasks and comment.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="medium"
                label="Enter KPI metric"
                value={currentArea}
                onChange={(e) => setCurrentArea(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addArea()}
              />
              <Button
                variant="contained"
                onClick={addArea}
                startIcon={<AddIcon />}
                sx={{ height: 56, whiteSpace: 'nowrap' }}
              >
                Add
              </Button>
            </Box>
            <List>
              {areas.map((area, index) => (
                <ListItem 
                  key={index}
                  sx={{
                    bgcolor: 'primary.light',
                    borderRadius: 1,
                    mb: 1,
                    '& .MuiListItemText-primary': {
                      color: 'white',
                      fontWeight: 'bold'
                    }
                  }}
                >
                  <ListItemText primary={area} />
                </ListItem>
              ))}
            </List>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, borderLeft: '3px solid', borderColor: 'primary.main', pl: 1, mb: 2 }}>
              KPI Metrics
            </Typography>
            {areas.map((area, index) => (
              <Paper key={index} sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {area}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Comment
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Add any notes or context for this area..."
                  value={comments[area] || ''}
                  onChange={(e) => updateComment(area, e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    size="medium"
                    label="Enter task"
                    value={currentTasks[area] || ''}
                    onChange={(e) => updateTaskInput(area, e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTask(area)}
                  />
                  <Button
                    variant="contained"
                    onClick={() => addTask(area)}
                    startIcon={<AddIcon />}
                    sx={{ height: 56, whiteSpace: 'nowrap' }}
                  >
                    Add
                  </Button>
                </Box>
                <List>
                  {(tasks[area] || []).map((task, taskIndex) => (
                    <ListItem
                      key={taskIndex}
                      sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <ListItemText
                        primary={task.name}
                        sx={{ flex: 1 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={task.priority || 'low'}
                          onChange={(e) => {
                            const updated = [...(tasks[area] || [])];
                            updated[taskIndex] = { ...updated[taskIndex], priority: e.target.value };
                            setTasks({ ...tasks, [area]: updated });
                          }}
                          sx={{ '& .MuiSelect-select': { py: 1 } }}
                        >
                          {Object.entries(priorityLabels).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                              <Chip label={label} color={priorityColors[value]} size="small" sx={{ minWidth: 70 }} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                          value={task.status}
                          onChange={(e) => updateTaskStatus(area, taskIndex, e.target.value)}
                          sx={{ '& .MuiSelect-select': { py: 1 } }}
                        >
                          <MenuItem value="not_started">Not Started</MenuItem>
                          <MenuItem value="in_progress">In Progress</MenuItem>
                          <MenuItem value="not_achieved">Not Achieved</MenuItem>
                          <MenuItem value="achieved">Achieved</MenuItem>
                        </Select>
                      </FormControl>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ))}

            <Divider sx={{ mb: 3 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, borderLeft: '3px solid', borderColor: 'primary.main', pl: 1, mb: 2 }}>
              Review Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  When should this KPI be reviewed? <Typography component="span" color="error">*</Typography>
                </Typography>
                <DatePicker
                  label="Next Review Date *"
                  value={reviewDate}
                  onChange={(date) => setReviewDate(date)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      error={params.error || false}
                      helperText="Required — used to track deadline-aware progress"
                    />
                  )}
                />
              </Paper>
            </LocalizationProvider>

            {isCoach && (
              <>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, borderLeft: '3px solid', borderColor: 'primary.main', pl: 1, mb: 2 }}>
                  Assignment
                </Typography>
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Assign to a user or save as a reusable template
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  If no user is selected, this KPI will be saved as a generic template that can be assigned later.
                </Alert>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Company (optional)</InputLabel>
                  <Select
                    value={selectedCompanyId}
                    onChange={(e) => { setSelectedCompanyId(e.target.value); setSelectedUserId(''); }}
                    label="Company (optional)"
                  >
                    <MenuItem value="">None (Generic template)</MenuItem>
                    {companies.map(c => (
                      <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Assign to User (optional)</InputLabel>
                  <Select
                    value={selectedUserId}
                    onChange={(e) => { setSelectedUserId(e.target.value); setSaveAsTemplate(false); }}
                    label="Assign to User (optional)"
                    disabled={!selectedCompanyId}
                  >
                    <MenuItem value="">None (Save as template)</MenuItem>
                    {usersInSelectedCompany.map(u => (
                      <MenuItem key={u._id} value={u._id}>{u.name} ({u.role})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedUserId && (
                  <FormControlLabel
                    control={<Checkbox checked={saveAsTemplate} onChange={(e) => setSaveAsTemplate(e.target.checked)} />}
                    label="Save as template"
                  />
                )}
              </Paper>
              </>
            )}
            {(isManager || isCompanyAdmin) && (
              <>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, borderLeft: '3px solid', borderColor: 'primary.main', pl: 1, mb: 2 }}>
                  Assignment
                </Typography>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Optionally assign this KPI to one of your team members
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Assign to User (optional)</InputLabel>
                    <Select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      label="Assign to User (optional)"
                    >
                      <MenuItem value="">None (assign to myself)</MenuItem>
                      {allUsers.map(u => (
                        <MenuItem key={u._id} value={u._id}>{u.name} ({u.role})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </>
            )}
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Container>
      <Toast 
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 0.5 }}>
            <Typography variant="h4">
              KPI Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Step {activeStep + 1} of {TOTAL_STEPS}
            </Typography>
          </Box>
          <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ fontWeight: '550' }}>
            {activeStep === 0
              ? 'Define KPI Metrics — Name your KPI & set metrics'
              : 'Build your KPI — Expected Measurable Outcome · Comments · Review Date'}
          </Typography>
        </Box>
        {getStepContent(activeStep)}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1, height: 52, px: 4 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            sx={{ height: 52, px: 4 }}
          >
            {activeStep === TOTAL_STEPS - 1 ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default KPIPage;
